<template>
  <v-container class="pa-0" fluid>
    <v-row class="ma-0 ma-md-2">
      <v-col cols="12">
        <v-card>
          <v-toolbar flat>
            <v-toolbar-title>
              <v-avatar height="32px" class="mx-auto">
                <videon-logo />
              </v-avatar>
              Admin API Usage
            </v-toolbar-title>
          </v-toolbar>
          <v-divider/>
          <v-card-text>
            <v-alert dense outlined type="error" v-if="error">{{ errorText }}</v-alert>
            <h3 class="mb-2">Quota ledger — current period</h3>
            <p class="caption mb-2">
              Requests reserved on each organization's quota counter for the
              current period{{ quotaPeriodCaption }}. This is the billing ledger,
              so it only counts enforce-mode requests and does not follow the
              range selected below.
            </p>
            <v-row class="mx-0">
              <v-col cols="12" md="7">
                <v-text-field
                  v-model="filterOrg"
                  label="Organization GUID"
                  outlined
                  dense
                  hide-details
                  @keyup.enter="load"
                />
              </v-col>
              <v-col cols="6" md="2">
                <v-btn color="primary" block @click="load">Refresh</v-btn>
              </v-col>
              <v-col cols="6" md="3">
                <v-btn outlined block @click="exportOrg" :disabled="!filterOrg">
                  Export organization
                </v-btn>
              </v-col>
            </v-row>
            <v-data-table
              :headers="headers"
              :items="currentQuotaRows"
              :loading="loading"
              hide-default-footer
              :items-per-page="20"
              class="mb-6"
            />

            <h3 class="mb-2">Environment usage explorer</h3>
            <p class="caption">
              Reporting uses completed hourly or daily aggregates and can lag live
              traffic by about one hour. It does not replace the per-organization
              quota counter.
            </p>
            <period-selection
              v-model="period"
              initialRange="1day"
              :maxCustomDays="31"
              allowMonthlyUsage
              allowQuotaPeriod
              :quotaPeriodBounds="currentQuotaPeriodBounds"
            />

            <v-row class="mx-0">
              <v-col cols="12" md="3">
                <v-select
                  v-model="overlayKind"
                  :items="overlayKinds"
                  label="Graph overlay"
                  dense
                  outlined
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="7">
                <v-combobox
                  v-if="overlayKind"
                  v-model="overlayKey"
                  :items="overlayChoices"
                  item-text="text"
                  item-value="value"
                  label="Organization GUID or endpoint"
                  dense
                  outlined
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="2" class="d-flex align-center">
                <v-btn color="primary" block :disabled="!period" @click="fetchActivity">
                  Refresh
                </v-btn>
              </v-col>
            </v-row>

            <v-alert dense outlined type="info" class="mt-3" v-if="completeThrough">
              Aggregates are complete through {{ formatUsageTime(completeThrough) }}.
              Partial boundary hours or days are omitted.
            </v-alert>
            <v-alert dense outlined type="warning" v-if="activity && !activity.complete">
              {{ activity.missingBuckets.length }} of
              {{ activity.totalSeries.values.length }}
              {{ activity.interval === '1d' ? 'day' : 'hour' }}(s) in this range
              have not been aggregated yet, and count as zero here. Totals and
              rankings are undercounted until those buckets are materialized, so
              they will not reconcile with the quota ledger.
            </v-alert>
            <v-alert dense outlined type="error" v-if="chartError">{{ chartErrorText }}</v-alert>
            <v-progress-linear v-if="chartLoading" color="primary" indeterminate class="my-3" />

            <videon-chart
              v-if="activity"
              heading="Environment PAT requests"
              unit="requests"
              :deviceMetrics="activity"
              :colorByDevice="false"
              :pointStyles="['circle', 'triangle']"
              :pointStyleInLegend="true"
              :pointStyleInTooltip="true"
              :showLegend="true"
              :startTime="activityStart"
              :endTime="activityEnd"
            />

            <v-row v-if="activity" class="mt-3">
              <v-col cols="12" lg="4">
                <h3 class="mb-2">Top organizations {{ rangeSuffix }}</h3>
                <p class="caption mb-2">
                  All metered calls in the selected range, including observe
                  mode, so these totals will not match the quota ledger above.
                </p>
                <v-data-table
                  :headers="organizationRankingHeaders"
                  :items="topOrganizationRows"
                  hide-default-footer
                  dense
                  @click:row="selectOrgOverlay"
                />
              </v-col>
              <v-col cols="12" lg="4">
                <h3 class="mb-2">Top endpoints {{ rangeSuffix }}</h3>
                <v-data-table
                  :headers="rankingHeaders('Endpoint')"
                  :items="activity.endpoints"
                  hide-default-footer
                  dense
                  @click:row="selectEndpointOverlay"
                />
              </v-col>
              <v-col cols="12" lg="4">
                <h3 class="mb-2">Top PATs {{ rangeSuffix }}</h3>
                <v-data-table
                  :headers="tokenHeaders"
                  :items="activity.tokens"
                  hide-default-footer
                  dense
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import moment from 'moment'
  import { quotaPeriodBounds } from '../objects/CloudProApi'
  import PeriodSelection from '../components/common/PeriodSelection.vue'
  import AdminApiUsageActivity from '../components/common/metrics/AdminApiUsageActivity.js'

  export default {
    name: 'AdminApiUsage',
    components: {
      PeriodSelection,
    },
    data() {
      return {
        loading: false,
        error: false,
        errorText: '',
        filterOrg: '',
        organizations: [],
        quotaPeriod: null,
        orgNames: {},
        period: null,
        activity: null,
        chartLoading: false,
        chartError: false,
        chartErrorText: '',
        overlayKind: null,
        overlayKey: '',
        overlayKinds: [
          { text: 'None — environment total only', value: null },
          { text: 'Organization', value: 'org' },
          { text: 'Endpoint', value: 'endpoint' },
        ],
        headers: [
          { text: 'Organization', value: 'org_name' },
          { text: 'GUID', value: 'org_guid' },
          { text: 'Period', value: 'period_key' },
          { text: 'Used', value: 'used' },
        ],
        tokenHeaders: [
          { text: 'Prefix', value: 'token_prefix' },
          { text: 'Username', value: 'username' },
          { text: 'Email', value: 'email' },
          { text: 'Comment', value: 'comment' },
          { text: 'Calls', value: 'used' },
        ],
        organizationRankingHeaders: [
          { text: 'Organization', value: 'org_name' },
          { text: 'Calls', value: 'used' },
        ],
      }
    },
    computed: {
      currentQuotaPeriodBounds() {
        return quotaPeriodBounds(this.quotaPeriod)
      },
      quotaPeriodCaption() {
        if (!this.quotaPeriod) {
          return ''
        }
        return ' (' + this.formatUsageTime(this.quotaPeriod.period_start) +
          ' – ' + this.formatUsageTime(this.quotaPeriod.reset_at) + ')'
      },
      rangeSuffix() {
        // The covered window comes from the response, not the picker. Only
        // whole materialized buckets are summed, so labeling these tables with
        // the requested range would overstate what the numbers include.
        if (!this.activityStart || !this.activityEnd) {
          return ''
        }
        const start = moment(this.activityStart)
        const end = moment(this.activityEnd).add(
          this.activity.interval === '1d' ? 1 : 0, 'days',
        ).add(this.activity.interval === '1h' ? 1 : 0, 'hours')
        const format = 'MMM D, HH:mm'
        const endText = start.isSame(end, 'day') ? end.format('HH:mm') : end.format(format)
        return '(' + start.format(format) + ' – ' + endText + ')'
      },
      currentQuotaRows() {
        return this.organizations.map((row) => ({
          ...row,
          org_name: this.orgNames[row.org_guid] || row.org_guid,
        }))
      },
      topOrganizationRows() {
        if (!this.activity) {
          return []
        }
        return this.activity.organizations.map((row) => ({
          ...row,
          org_name: this.orgNames[row.key] || row.key,
        }))
      },
      completeThrough() {
        return this.activity && this.activity.completeThrough
      },
      activityStart() {
        return this.activity && this.activity.snapBounds && this.activity.snapBounds.start_time
      },
      activityEnd() {
        return this.activity && this.activity.snapBounds && this.activity.snapBounds.end_time
      },
      overlayChoices() {
        if (!this.activity) {
          return []
        }
        const rows = this.overlayKind === 'org'
          ? this.activity.organizations
          : this.activity.endpoints
        return rows.map((row) => ({
          text: this.overlayKind === 'org'
            ? (this.orgNames[row.key] || row.key)
            : row.key,
          value: row.key,
        }))
      },
    },
    watch: {
      period() {
        this.fetchActivity()
      },
      overlayKind() {
        this.overlayKey = ''
      },
    },
    mounted() {
      this.load()
      this.loadOrgNames()
    },
    methods: {
      // Read /orgs directly rather than through $organizations. This page is
      // environment-wide, so /orgs answers in admin view and omits `access` for
      // orgs the admin is not a member of. VideonOrganization treats that as
      // incomplete and self-fetches /orgs/{guid}, which is org scoped and 401s
      // once per org in the environment.
      async loadOrgNames() {
        const names = {}
        let paginationToken = null
        try {
          do {
            const params = { pagination_size: 50 }
            if (paginationToken) {
              params.pagination_token = paginationToken
            }
            const response = await this.axios.get('/orgs', { params })
            for (const org of response.data.orgs || []) {
              names[org.org_guid] = org.org_name
            }
            paginationToken = response.data.pagination_token || null
          } while (paginationToken)
        } catch (error) {
          // Names are display sugar. Every table falls back to the GUID.
        }
        this.orgNames = names
        if (this.activity) {
          this.activity.organizationNames = names
        }
      },
      load() {
        this.loading = true
        const params = this.filterOrg ? { org_guid: this.filterOrg } : {}
        this.axios.get('/usage/admin', { params }).then((response) => {
          this.organizations = response.data.organizations || []
          this.quotaPeriod = response.data.quota_period || null
          this.loading = false
        }).catch((error) => {
          this.error = true
          this.errorText = this.$helpers.parseError(error)
          this.loading = false
        })
      },
      exportOrg() {
        this.axios.get('/usage/export', {
          params: { org_guid: this.filterOrg, format: 'json' },
        }).then((response) => {
          const blob = new Blob(
            [JSON.stringify(response.data, null, 2)],
            { type: 'application/json' },
          )
          this.$helpers.downloadFile(`api-usage-${this.filterOrg}.json`, blob)
        }).catch((error) => {
          this.error = true
          this.errorText = this.$helpers.parseError(error)
        })
      },
      rankingHeaders(label) {
        return [
          { text: label, value: 'key' },
          { text: 'Calls', value: 'used' },
        ]
      },
      formatUsageTime(value) {
        return value ? moment(value).format('MMM D, YYYY HH:mm Z') : 'unknown'
      },
      selectedOverlayKey() {
        if (!this.overlayKey) {
          return ''
        }
        return typeof this.overlayKey === 'string'
          ? this.overlayKey
          : this.overlayKey.value
      },
      fetchActivity() {
        if (!this.period) {
          return
        }
        if (!this.activity) {
          this.activity = new AdminApiUsageActivity()
        }
        this.activity.organizationNames = this.orgNames
        const days = moment(this.period.end).diff(moment(this.period.start), 'days', true)
        this.activity.start_time = this.period.start
        this.activity.end_time = this.period.end
        this.activity.interval = days > 7 ? '1d' : '1h'
        const key = this.selectedOverlayKey()
        this.activity.overlay = this.overlayKind && key
          ? { kind: this.overlayKind, key }
          : null
        this.chartLoading = true
        this.chartError = false
        this.activity.fetch().then(() => {
          this.chartLoading = false
        }).catch((error) => {
          this.chartError = true
          this.chartErrorText = this.$helpers.parseError(error)
          this.chartLoading = false
        })
      },
      selectOrgOverlay(row) {
        this.overlayKind = 'org'
        this.$nextTick(() => {
          this.overlayKey = row.key
          this.fetchActivity()
        })
      },
      selectEndpointOverlay(row) {
        this.overlayKind = 'endpoint'
        this.$nextTick(() => {
          this.overlayKey = row.key
          this.fetchActivity()
        })
      },
    }
  }
</script>
